// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@loa/ui-components';
import { ChevronDownIcon } from '../loa';
import './NudgeOutstandingButton.css';

export interface NudgeOutstandingButtonProps {
  /** Bulk-nudge every request currently waiting on a client. */
  onNudgeClients: () => void;
  /** Bulk-nudge every request currently waiting on a provider. */
  onNudgeProviders: () => void;
  disabled?: boolean;
}

/**
 * Outlined "Nudge Outstanding" button that opens a small action menu with
 * two options — Nudge Clients / Nudge Providers. The popover is portalled to
 * the body (matching the dashboard FilterDropdown pattern) so it isn't
 * clipped by the table card's overflow.
 */
export const NudgeOutstandingButton: React.FC<NudgeOutstandingButtonProps> = ({
  onNudgeClients,
  onNudgeProviders,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPosition({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(t) &&
        triggerRef.current &&
        !triggerRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const select = (handler: () => void) => {
    setOpen(false);
    handler();
  };

  const popover =
    open && position
      ? createPortal(
          <div
            ref={popoverRef}
            className="nudge-outstanding__menu"
            role="menu"
            style={{ top: position.top, left: position.left, minWidth: position.width }}
            data-testid="nudge-outstanding-menu"
          >
            <button
              type="button"
              role="menuitem"
              className="nudge-outstanding__option"
              onClick={() => select(onNudgeClients)}
              data-testid="nudge-clients-option"
            >
              Nudge Clients
            </button>
            <button
              type="button"
              role="menuitem"
              className="nudge-outstanding__option"
              onClick={() => select(onNudgeProviders)}
              data-testid="nudge-providers-option"
            >
              Nudge Providers
            </button>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={triggerRef} className="nudge-outstanding">
      <Button
        variant="outlined"
        colour="purple"
        size="lg"
        iconRight={<ChevronDownIcon />}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="nudge-outstanding-button"
      >
        Nudge Outstanding
      </Button>
      {popover}
    </div>
  );
};
